/**
 * [TD-89 v2 Verdict O.A - DraftRoom + SnakeDraftRoom mount]
 *
 * Commissioner-only "Manage Managers" dropdown affordance for the draft room
 * commissioner controls strip. Shipped alongside `DraftRoomAbandonButton`.
 *
 * Why this exists as a small wrapper rather than mounting ManagerActionMenu
 * directly on inline manager tiles: DraftRoom.jsx and SnakeDraftRoom.jsx do
 * not render a top-level manager-list grid the way DraftLobby does. The
 * canonical mid-draft commissioner action surface IS the commissioner
 * controls slot (already houses Pause + Resume + Abandon). This dropdown
 * lists every non-commissioner-self manager with the `ManagerActionMenu`
 * kebab next to each name so the Convert action remains reachable mid-draft.
 *
 * Standings deferral per Spec Section 4.11: if the season standings page
 * needs the menu, that's TD-89.1 conditional follow-up.
 */
import { useEffect, useRef, useState } from "react";
import { Users, ChevronDown } from "lucide-react";
import ManagerActionMenu from "../common/ManagerActionMenu";

export default function CommissionerManagersDropdown({ league, isCommissioner, isDark = true, onMutated }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!isCommissioner) return null;
  const targets = (league?.managers || []).filter((m) => m.user_id !== league?.commissioner_id);
  if (targets.length === 0) return null;

  const ddBg = isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-white border-black/[0.10]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.55)]" : "text-[rgba(0,0,0,0.55)]";

  return (
    <div ref={rootRef} className="relative inline-flex" data-testid="commissioner-managers-dropdown-root">
      <button
        data-testid="commissioner-managers-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 px-3 py-1.5 font-display font-bold text-xs uppercase tracking-wider rounded-md transition-colors ${
          isDark ? "text-[#A0A0A0] hover:text-[#FF6B00] hover:bg-white/[0.06]" : "text-[#666] hover:text-[#FF6B00] hover:bg-black/[0.05]"
        }`}
        title="Manage managers"
      >
        <Users size={14} /> Managers <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="commissioner-managers-dropdown-panel"
          className={`absolute right-0 top-full mt-1 w-64 rounded-lg border shadow-xl overflow-hidden z-30 ${ddBg}`}
        >
          <div className={`px-3 py-2 border-b ${isDark ? "border-[#252525]" : "border-black/[0.08]"}`}>
            <div className={`font-display font-bold text-[10px] uppercase tracking-wider ${muted}`}>Convert manager to CPU</div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {targets.map((mgr) => {
              const isCpu = !!mgr.is_bot || mgr.first_name === "CPU";
              return (
                <div
                  key={mgr.user_id || mgr.id}
                  data-testid={`commissioner-managers-row-${mgr.user_id}`}
                  className={`flex items-center justify-between px-3 py-2 ${isDark ? "hover:bg-white/[0.04]" : "hover:bg-black/[0.04]"}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className={`font-body text-sm truncate ${fg}`}>{mgr.team_name || `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim()}</div>
                    <div className={`font-body text-[10px] truncate ${muted}`}>
                      {isCpu ? "CPU" : `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim()}
                    </div>
                  </div>
                  <ManagerActionMenu
                    league={league}
                    manager={{
                      user_id: mgr.user_id,
                      first_name: mgr.first_name,
                      last_name: mgr.last_name,
                      team_name: mgr.team_name,
                      is_cpu: isCpu,
                    }}
                    isCommissioner={isCommissioner}
                    isDark={isDark}
                    onMutated={() => { onMutated?.(); setOpen(false); }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
